import { Animation, createAnimation } from '@ionic/angular';

/**
 * Transition personnalisée : slide depuis la droite (forward) ou depuis la gauche (backward)
 * Utilisée pour toutes les navigations de l'application
 */
export function slideTransition(
  baseEl: HTMLElement,
  opts?: any
): Animation {
  const OFF_RIGHT = '100%';
  const OFF_LEFT = '-100%';
  const CENTER = '0%';

  const enteringEl = opts?.enteringEl;
  const leavingEl = opts?.leavingEl;
  
  // Détecter la direction : 'forward' pour navigateForward, 'back' pour navigateBack
  // Ionic passe automatiquement la direction dans opts.direction
  const direction = opts?.direction || 'forward';

  // Déterminer la direction de l'animation
  // Forward: nouvelle page vient de la droite (100%) et glisse vers le centre (devant l'ancienne)
  // Backward: nouvelle page vient de la gauche (-100%) et glisse vers le centre (devant l'ancienne)
  const enteringFrom = direction === 'forward' ? OFF_RIGHT : OFF_LEFT;
  
  // L'ancienne page glisse dans la direction opposée pour créer l'effet de profondeur
  // Forward: ancienne page glisse vers la gauche (-30%) pour rester visible en arrière-plan
  // Backward: ancienne page glisse vers la droite (30%) pour rester visible en arrière-plan
  const leavingTo = direction === 'forward' ? '-30%' : '30%';

  // Configuration du conteneur de base avec fond blanc
  baseEl.style.setProperty('position', 'relative');
  baseEl.style.setProperty('overflow', 'hidden');
  baseEl.style.setProperty('width', '100%');
  baseEl.style.setProperty('height', '100%');
  baseEl.style.setProperty('background-color', '#ffffff');

  // S'assurer que les éléments existent
  if (!enteringEl) {
    return createAnimation();
  }

  // Appliquer les styles directement aux éléments avant l'animation
  if (enteringEl) {
    enteringEl.style.setProperty('position', 'absolute');
    enteringEl.style.setProperty('top', '0');
    enteringEl.style.setProperty('left', '0');
    enteringEl.style.setProperty('width', '100%');
    enteringEl.style.setProperty('height', '100%');
    enteringEl.style.setProperty('z-index', '10'); // Nouvelle page devant
    enteringEl.style.setProperty('background-color', '#ffffff');
    enteringEl.style.setProperty('will-change', 'transform');
    enteringEl.style.setProperty('overflow', 'hidden');
    enteringEl.style.setProperty('box-shadow', '0 0 20px rgba(0, 0, 0, 0.1)'); // Ombre pour effet de profondeur
    
    // Ensure ion-content inside has white background
    const ionContents = enteringEl.querySelectorAll('ion-content');
    ionContents.forEach((content: any) => {
      if (content.shadowRoot) {
        const scrollElement = content.shadowRoot.querySelector('.inner-scroll') || 
                             content.shadowRoot.querySelector('.scroll-content');
        if (scrollElement) {
          scrollElement.style.setProperty('background-color', '#ffffff');
        }
      }
      content.style.setProperty('--background', '#ffffff');
    });
  }

  if (leavingEl) {
    leavingEl.style.setProperty('position', 'absolute');
    leavingEl.style.setProperty('top', '0');
    leavingEl.style.setProperty('left', '0');
    leavingEl.style.setProperty('width', '100%');
    leavingEl.style.setProperty('height', '100%');
    leavingEl.style.setProperty('z-index', '9'); // Ancienne page derrière
    leavingEl.style.setProperty('background-color', '#ffffff');
    leavingEl.style.setProperty('will-change', 'transform');
    leavingEl.style.setProperty('overflow', 'hidden');
    leavingEl.style.setProperty('opacity', '0.95'); // Légère transparence pour effet de profondeur
    
    // Ensure ion-content inside has white background
    const ionContents = leavingEl.querySelectorAll('ion-content');
    ionContents.forEach((content: any) => {
      if (content.shadowRoot) {
        const scrollElement = content.shadowRoot.querySelector('.inner-scroll') || 
                             content.shadowRoot.querySelector('.scroll-content');
        if (scrollElement) {
          scrollElement.style.setProperty('background-color', '#ffffff');
        }
      }
      content.style.setProperty('--background', '#ffffff');
    });
  }

  // Animation d'entrée de la nouvelle page
  const enteringAnimation = createAnimation()
    .addElement(enteringEl)
    .duration(650) // Augmenté de 500ms à 650ms pour ralentir l'animation
    .easing('cubic-bezier(0.36, 0.66, 0.04, 1)') // Smooth easing for better feel
    .fromTo('transform', `translateX(${enteringFrom})`, `translateX(${CENTER})`)
    .fromTo('opacity', 1, 1)
    .afterStyles({
      position: 'relative',
      'z-index': '1',
      'will-change': 'auto', // Reset will-change after animation
      'background-color': '#ffffff',
      'overflow': 'visible',
      'box-shadow': 'none' // Retirer l'ombre après l'animation
    })
    .afterAddWrite(() => {
      // Ensure ion-content maintains white background after animation
      const ionContents = enteringEl.querySelectorAll('ion-content');
      ionContents.forEach((content: any) => {
        content.style.setProperty('--background', '#ffffff');
        if (content.shadowRoot) {
          const scrollElement = content.shadowRoot.querySelector('.inner-scroll') || 
                               content.shadowRoot.querySelector('.scroll-content');
          if (scrollElement) {
            scrollElement.style.setProperty('background-color', '#ffffff');
          }
        }
      });
    });

  // Créer l'animation principale
  const rootAnimation = createAnimation()
    .duration(650) // Augmenté de 500ms à 650ms pour ralentir l'animation
    .easing('cubic-bezier(0.36, 0.66, 0.04, 1)')
    .addAnimation(enteringAnimation);

  // Animation de sortie de l'ancienne page (seulement si elle existe)
  if (leavingEl) {
    const leavingAnimation = createAnimation()
      .addElement(leavingEl)
      .duration(650) // Augmenté de 500ms à 650ms pour ralentir l'animation
      .easing('cubic-bezier(0.36, 0.66, 0.04, 1)') // Same easing for consistency
      .fromTo('transform', `translateX(${CENTER})`, `translateX(${leavingTo})`)
      .fromTo('opacity', 1, 0.95) // Légère transparence pendant l'animation
      .afterClearStyles(['position', 'top', 'left', 'width', 'height', 'z-index', 'will-change', 'background-color', 'opacity', 'box-shadow']);

    rootAnimation.addAnimation(leavingAnimation);
  }

  return rootAnimation;
}

